/* eslint-disable no-shadow */

import Vue from 'vue';

import date from '@/mixins/date';
import Common from '@/store/common';
import router from '@/router/index';

const state = {
  surveyResponse: {},
  surveys: [],
  surveysLoaded: false,
  surveyLoaded: false,
  pages: [],
  activeSurvey: {},
  activePage: 0,
  submissionAttempted: false,
};

const getters = {
  activePage: state => state.pages.find(page => page.id === state.activePage) || {},
  activeFields: (state, getters) => getters.activePage.fields,
  incompleteSurveys: state => state.surveys
    .filter((survey) => {
      return !survey.response || (survey.response && survey.response.complete !== true) || survey.due_new_response;
    })
    .sort((a, b) => date.methods.parseDateTime(b.invited_on) - date.methods.parseDateTime(a.invited_on))
    .filter((survey, index, surveys) => {
      return surveys.map(s => s.id).indexOf(survey.id) === index;
    }),
  previousPage: (state) => {
    if (state.pages.length === 0) return {};

    const activePageIndex = state.pages.findIndex(page => page.id === state.activePage);
    return state.pages[activePageIndex - 1];
  },
  nextPage: state => (
    state.pages.length > 0 ? state.pages.find(page => page.id === state.surveyResponse.next_page) : {}
  ),
  firstPage: state => (state.pages.length > 0 ? state.pages[0] : {}),
  onFirstPage: (state, getters) => getters.firstPage === getters.activePage,
  lastPage: state => (state.pages.length > 0 ? state.pages[state.pages.length - 1] : {}),
  onLastPage: (state, getters) => getters.lastPage === getters.activePage,
  responseStatus: () => (survey) => {
    if (survey.response && !survey.due_new_response) {
      return survey.response.complete ? 'Complete' : 'In Progress';
    }
    return 'Invited';
  },
  branding: (state) => state.activeSurvey.branding
};

const mutations = {
  startSurvey(state, survey) {
    state.surveyResponse = survey;
    state.activePage = survey.next_page;
  },

  setSurveys(state, surveys) {
    Vue.set(state, 'surveys', surveys);
  },

  setSurveysLoadedState(state, loading) {
    state.surveysLoaded = loading;
  },

  setSurveyLoadedState(state, loading) {
    state.surveyLoaded = loading;
  },

  setActiveSurvey(state, survey) {
    state.activeSurvey = survey;
  },

  setSurveyResponse(state, surveyResponse) {
    state.surveyResponse = surveyResponse;
  },

  setPages(state, pages) {
    state.pages = pages;
  },

  setActivePage(state, page) {
    state.activePage = page;
  },

  setSubmissionAttempted(state, attempted) {
    state.submissionAttempted = attempted;
  },

  addFieldsToPage(state, { page, fields }) {
    Vue.set(page, 'fields', fields);
  },
};

const actions = {
  fetchSurveys({ commit, dispatch }) {
    commit('setSurveysLoadedState', false);
    return Vue.axios.get('survey-invites')
      .then((response) => {
        commit('setSurveys', response.data.surveyinvites.map((invite) => {
          const survey = invite.survey;
          survey.invite_id = invite.id;
          survey.response = invite.last_survey_response;
          survey.invited_on = invite.created_at;
          survey.has_last_response = !!invite.last_survey_response;
          survey.due_new_response = invite.due_new_response;
          return survey;
        }));
        commit('setSurveysLoadedState', true);
      })
      .catch(error => Common.handleError(dispatch, error.response));
  },

  fetchSurvey({ commit, dispatch }, survey) {
    commit('setSurveyLoadedState', false);
    return Vue.axios.get(`surveys/${survey}`)
      .then((response) => {
        commit('setActiveSurvey', response.data.survey);
        commit('setSurveyLoadedState', true);
      })
      .catch(error => Common.handleError(dispatch, error.response));
  },

  fetchSurveyResponse({ commit, dispatch }, { surveyId, authId }) {
    return Vue.axios.get(`survey-responses/${surveyId}${authId ? `?auth_id=${authId}` : ''}`)
      .then((response) => {
        commit('setSurveyResponse', response.data.surveyresponse);
      })
      .catch(error => Common.handleError(dispatch, error.response));
  },

  fetchPages({ commit, dispatch }, survey) {
    return Vue.axios.get(`surveys/${survey}/pages`)
      .then((response) => {
        commit('setPages', response.data.page);
      }).catch(error => Common.handleError(dispatch, error.response));
  },

  renderPage({ commit, dispatch, getters }, { survey, page }) {
    return Vue.axios.get(`surveys/${survey}/pages/${page}/render`)
      .then((response) => {
        commit('setActivePage', page);
        commit('addFieldsToPage', { page: getters.activePage, fields: response.data.page.fields });
      }).catch(error => Common.handleError(dispatch, error.response));
  },

  startSurvey({ commit, dispatch }, { survey, surveyInvite, useLastResponse = false }) {
    return dispatch('fetchPages', survey).then(async () => {
      await Vue.axios.post(
        `survey-responses/${survey}`,
        { survey_invite_id: surveyInvite, use_last_response: useLastResponse })
        .then((response) => {
          if (useLastResponse) {
            router.push({
              name: 'survey-resume',
              params: {
                surveyRef: survey,
                surveyId: response.data.surveyresponse.id,
                surveyInviteId: surveyInvite,
                pageId: response.data.surveyresponse.first_page,
              },
            });
          } else {
            commit('startSurvey', response.data.surveyresponse);
            dispatch('renderPage', { survey, page: response.data.surveyresponse.next_page });
          }
        }).catch(error => Common.handleError(dispatch, error.response));
    });
  },

  resumeSurvey({ commit, dispatch }, { survey, surveyResponse, page, authId }) {
    return dispatch('fetchPages', survey).then(async () => {
      await dispatch('fetchSurveyResponse', { surveyId: parseInt(surveyResponse), authId }).then(() => {
        dispatch('renderPage', { survey, page: parseInt(page) });
      });
    });
  },

  savePage(
    { commit, dispatch, state },
    { survey, auth, page, model, form, skipPageChange = false, skipComplete = false }) {
    return Vue.axios.put(
      `survey-responses/${survey}`,
      { page_id: page, auth_id: auth, data: model, skip_complete: skipComplete },
    )
      .then((response) => {
        if (response.data.surveyresponse.complete) {
          commit('setSurveyResponse', response.data.surveyresponse);
          router.push({ name: 'survey-complete' });
        } else if (!skipPageChange) {
          const surveyId = response.data.surveyresponse.id;
          const nextPage = state.pages[state.pages.findIndex(p => p.id === page) + 1];
          const pageId = nextPage !== undefined ? nextPage.id : response.data.surveyresponse.next_page;
          router.push({ name: 'survey-resume', params: { surveyId, pageId } });
        }
      }).catch(error => Common.handleError(dispatch, error.response, form));
  },

};

export default {
  state,
  getters,
  mutations,
  actions,
};
